import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { AdminBar, EditButton, DeleteButton } from '@/components/AdminBar';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, Download, FileText, Image, Package, Mail, FlaskConical, File } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_ICONS = {
  scheda_paziente: FileText,
  brochure: FileText,
  presentazione: FileText,
  social_kit: Image,
  email_template: Mail,
  scientific_dossier: FlaskConical,
  altro: File,
};

const CAT_COLORS = {
  scheda_paziente: 'bg-[#5f7d52]/10 text-[#4a5f43]',
  brochure: 'bg-blue-50 text-blue-600',
  presentazione: 'bg-purple-50 text-purple-600',
  social_kit: 'bg-pink-50 text-pink-600',
  email_template: 'bg-amber-50 text-amber-600',
  scientific_dossier: 'bg-[#12100c]/10 text-[#12100c]',
};

const PROF_LABELS = {
  medico: 'Medico',
  veterinario: 'Veterinario',
  naturopata: 'Naturopata',
  operatore_olistico: 'Operatore olistico',
  erborista_parafarmacia: 'Erborista / parafarmacia',
  informatore_farmaceutico: 'Inf. Farmaceutico',
  marketer_influencer: 'Marketer / influencer',
};

export default function MaterialsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState('tutti');
  const [loading, setLoading] = useState(true);
  const canEdit = user?.is_admin || user?.is_editor || user?.role === 'admin' || user?.role === 'editor';

  const fetchMaterials = async (cat = '') => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/materials${cat && cat !== 'tutti' ? `?category=${cat}` : ''}`);
      setMaterials(resp.data.materials);
      if (resp.data.categories) setCategories(resp.data.categories);
    } catch { /* */ }
    setLoading(false);
  };

  useEffect(() => { fetchMaterials(); }, []);

  const handleFilter = (cat) => {
    setActiveCat(cat);
    fetchMaterials(cat);
  };

  return (
    <div className="max-w-5xl" data-testid="materials-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#12100c]">Materiali & Download</h1>
        <p className="text-sm text-[#4a4238] mt-2">Strumenti pronti per la tua attività</p>
      </div>

      <AdminBar section="materials" onAdd={() => navigate('/admin', { state: { tab: 'materials', action: 'new' } })} />

      {/* Category Filters */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-6 scrollbar-hide" style={{ scrollbarWidth: 'none' }} data-testid="material-filters">
        {categories.map(cat => (
          <button key={cat.id} onClick={() => handleFilter(cat.id)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
              activeCat === cat.id ? 'bg-[#12100c] text-white border-[#12100c]' : 'bg-white text-[#4a4238] border-gray-200 hover:border-[#5f7d52]'
            }`} data-testid={`mat-filter-${cat.id}`}>{cat.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#12100c]" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {materials.map(mat => {
              const Icon = CAT_ICONS[mat.category] || File;
              const colorClass = CAT_COLORS[mat.category] || 'bg-gray-50 text-gray-600';
              const isSoon = mat.coming_soon || !mat.file_url;

              return (
                <Card key={mat.id} className={`rounded-2xl border-gray-100 shadow-sm transition-all ${isSoon ? 'opacity-60' : 'hover:shadow-md hover:-translate-y-0.5'}`}
                  data-testid={`material-${mat.id}`}>
                  <CardContent className="p-5 relative">
                    {isSoon && (
                      <Badge className="absolute top-3 right-3 bg-[#c6a15b] text-[#12100c] border-none text-[9px] font-semibold">In arrivo</Badge>
                    )}

                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-xl ${colorClass} flex items-center justify-center mb-3`}>
                      <Icon className="w-6 h-6" strokeWidth={1.5} />
                    </div>

                    <h3 className="font-semibold text-sm text-[#12100c] mb-1 leading-snug">{mat.title}</h3>
                    <p className="text-xs text-[#4a4238] leading-relaxed mb-3 line-clamp-2">{mat.description}</p>

                    {/* Professional types */}
                    {mat.professional_types?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {mat.professional_types.map(pt => (
                          <Badge key={pt} variant="outline" className="text-[8px] border-gray-200 text-[#4a4238]">
                            {PROF_LABELS[pt] || pt}
                          </Badge>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-[10px] text-[#4a4238]">
                        {mat.file_type && <span className="uppercase font-semibold bg-gray-100 px-1.5 py-0.5 rounded">{mat.file_type}</span>}
                        {mat.file_size_kb && <span>{Math.round(mat.file_size_kb / 1024 * 10) / 10} MB</span>}
                        {mat.download_count > 0 && <span className="flex items-center gap-0.5"><Download className="w-3 h-3" /> {mat.download_count}</span>}
                      </div>
                      <Button size="sm" disabled={isSoon}
                        className={`rounded-full h-7 px-3 text-xs ${isSoon ? 'bg-gray-200 text-gray-400' : 'bg-[#12100c] hover:bg-[#5f7d52] text-white'}`}
                        data-testid={`download-${mat.id}`}>
                        <Download className="w-3 h-3 mr-1" /> Scarica
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Info Box */}
          <Card className="rounded-xl border-[#c6a15b]/20 shadow-sm mt-8" data-testid="materials-info-box">
            <CardContent className="p-4 sm:p-5 bg-[#c6a15b]/[0.05]">
              <p className="text-sm text-[#12100c]">
                <span className="font-semibold">Hai bisogno di materiale specifico?</span>{' '}
                Contattaci per richiedere materiali personalizzati per il tuo studio o attività.
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
